/** @jsxImportSource @emotion/react */
import {
  formatErrorResponse,
  useSession,
  Balances,
  formatCurrency,
} from '@quickbit/backoffice-core-frontend';
import {
  List,
  ListItem,
  Icon,
  useTheme,
  iconMap,
  IconName,
  Button,
  Snackbar,
  UtilityButton,
  Card,
  Typography,
  Input,
} from '@quickbit/qb-design-system';
import { useCallback, useState } from 'react';
import { useAsync, useAsyncFn, useMount, useUpdateEffect } from 'react-use';
import { QBLoader, Markdown } from 'components';
import { Permissions } from '../../constants';
import {
  reuploadTransactionToCM1,
  refundTransaction,
  searchTransactions,
  exportTransactions,
  getTransactionDetails,
  getUserBalance,
  createCryptoRefundPreview,
  getTravelRuleInfoStatus,
  completeCryptoRefund,
  type Transaction,
  type TravelRuleInfoStatus,
  getWalletAddresses,
  TransactionType,
} from './api';
import { usePoll } from './hooks/usePoll';
import { ManualReviewButtons } from './ManualReviewButtons';
import { TransactionItem } from './TransactionItem';

interface Props {
  userId: string;
}

export const Transactions = ({ userId }: Props) => {
  const session = useSession();
  const { palette, spacing, shape } = useTheme();
  const [selectedTxId, setSelectedTxId] = useState<string>();
  const [offsetTop, setOffsetTop] = useState<number>();
  const [showErrorAlert, setShowErrorAlert] = useState<boolean>(false);
  const [refundDestinationAddress, setRefundDestinationAddress] = useState('');
  const [travelRuleWindow, setTravelRuleWindow] = useState<Window | null>(null);

  const [{ value, loading }, refreshTransactions] = useAsyncFn(() => {
    return searchTransactions({ filters: { userId } });
  }, [userId]);

  const [{ loading: exportLoading }, exportTransactionsFn] =
    useAsyncFn(async () => {
      const blob = await exportTransactions({ userId });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${userId}-transactions.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    }, [userId]);
  const [{ value: balances, loading: balancesLoading }, refreshBalances] =
    useAsyncFn(() => {
      return getUserBalance(userId);
    }, [userId]);

  const { value: addresess, loading: addresessLoading } = useAsync(async () => {
    if (!balances) return null;
    return getWalletAddresses(userId, Object.keys(balances.balances));
  }, [userId, balances]);

  const { value: details, loading: detailsLoading } = useAsync(async () => {
    if (!selectedTxId) return null;
    return getTransactionDetails(selectedTxId);
  }, [selectedTxId]);

  const transactions = value
    ? Object.values(value.transactions).sort((a, b) =>
        new Date(a.createdAt) < new Date(b.createdAt) ? 1 : -1
      )
    : undefined;

  const [
    { error: reuploadTxError, loading: reuploadTXLoading },
    reuploadTxToCM1,
  ] = useAsyncFn(async () => {
    if (!selectedTxId) return;
    return reuploadTransactionToCM1(selectedTxId);
  }, [selectedTxId]);

  const [
    {
      value: refundCryptoValue,
      error: refundCryptoError,
      loading: refundCryptoLoading,
    },
    completeSend,
  ] = useAsyncFn((previewId: string) => completeCryptoRefund(previewId), []);

  const [
    {
      value: refundCryptoPreviewValue,
      error: refundCryptoPreviewError,
      loading: refundCryptoPreviewLoading,
    },
    refundCryptoFn,
  ] = useAsyncFn(async (transactionId, destinationAddress) => {
    const preview = await createCryptoRefundPreview(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      transactionId,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      destinationAddress
    );
    if (!preview.isInternal) {
      startTravelRulePolling();
      openTravelRuleForm(preview.previewId);
      return preview;
    } else {
      void completeSend(preview.previewId);
    }
  }, []);

  const getTravelRuleStatus = useCallback(async () => {
    if (refundCryptoPreviewValue)
      return getTravelRuleInfoStatus(refundCryptoPreviewValue.previewId);
    return Promise.resolve({ status: false });
  }, [refundCryptoPreviewValue]);

  const checkTravelRuleSuccess = useCallback(
    (data: TravelRuleInfoStatus) => {
      if (data.status) {
        if (travelRuleWindow) {
          travelRuleWindow.close();
          setTravelRuleWindow(null);
        }
        if (refundCryptoPreviewValue)
          void completeSend(refundCryptoPreviewValue.previewId);
        return true;
      }
      return null;
    },
    [travelRuleWindow, completeSend, refundCryptoPreviewValue]
  );

  const {
    start: startTravelRulePolling,
    error: travelRuleInfoError,
    isPolling: isPollingTravelRule,
  } = usePoll({
    poll: getTravelRuleStatus,
    onPoll: checkTravelRuleSuccess,
  });

  const [
    { value: refundValue, error: refundError, loading: refundLoading },
    refund,
  ] = useAsyncFn((transactionId) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return refundTransaction(transactionId);
  }, []);

  useMount(() => {
    void refreshTransactions();
    void refreshBalances();
  });

  useUpdateEffect(() => {
    if (
      reuploadTxError ||
      refundError ||
      refundCryptoPreviewError ||
      travelRuleInfoError ||
      refundCryptoError
    )
      setShowErrorAlert(true);
  }, [
    reuploadTxError,
    refundError,
    refundCryptoPreviewError,
    travelRuleInfoError,
    refundCryptoError,
  ]);

  const openTravelRuleForm = (previewId: string) => {
    const openedWindow = window.open(
      `${process.env.REACT_APP_QB_WEB_URL}/travel-rule-beneficiary-info/${previewId}`
    );
    setTravelRuleWindow(openedWindow);
  };

  const hasFCURefundPermission =
    session.authenticated &&
    session.permissions.includes(Permissions.PROCESS_FCU_REFUNDS);

  const hasCardRefundPermission =
    session.authenticated &&
    session.permissions.includes(Permissions.PROCESS_CARD_REFUNDS);

  return (
    <>
      <UtilityButton
        text="See user's wallets in Nexus"
        icon="wallet"
        css={{ maxWidth: 500, marginBottom: spacing.xs }}
        onClick={() => {
          const isProduction =
            process.env.REACT_APP_ENVIRONMENT === 'production';
          const link = `https://${
            isProduction ? 'portal' : 'testportal'
          }.quantoznexus.com/labelpartner/qbcustodian/customer/${userId}#custodian-balance`;
          window.open(link, '_blank');
        }}
      />

      <div css={{ display: 'flex', flexDirection: 'column', gap: spacing.xxs }}>
        <Typography variant="heading2">Balances</Typography>
        {(balancesLoading || addresessLoading) && <QBLoader />}
        {balances && addresess && (
          <Balances balances={balances.balances} addresses={addresess} />
        )}
      </div>

      <>
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: spacing.xxs,
          }}
        >
          <Typography variant="heading2">Transactions</Typography>
          <UtilityButton
            variant="secondary"
            text="Export"
            loading={exportLoading}
            disabled={exportLoading}
            onClick={exportTransactionsFn}
          />
        </div>
        {loading && <QBLoader />}
        <div
          css={[
            { display: 'grid', gap: spacing.xxs, position: 'relative' },
            selectedTxId && { gridTemplateColumns: '1fr 1fr' },
          ]}
        >
          <List>
            {transactions &&
              transactions.map((transaction) => (
                <ListItem
                  key={transaction.id}
                  css={{
                    background:
                      transaction.status === 'FAILED'
                        ? palette.danger.x200
                        : transaction.status === 'MANUAL_REVIEW'
                        ? palette.warning.x50
                        : 'inherit',
                  }}
                  icon={
                    transaction.amount.currencyCode in iconMap
                      ? (transaction.amount.currencyCode as IconName)
                      : undefined
                  }
                  title={transaction.type}
                  subtitle={new Date(transaction.createdAt).toLocaleString()}
                  content={formatCurrency(transaction.amountReference)}
                  subcontent={formatCurrency(transaction.amount)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedTxId(transaction.id);
                    setOffsetTop(
                      selectedTxId === transaction.id
                        ? undefined
                        : e.currentTarget.offsetTop
                    );
                  }}
                  action={
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTxId(
                          selectedTxId === transaction.id
                            ? undefined
                            : transaction.id
                        );
                      }}
                      css={{
                        svg: {
                          transform:
                            selectedTxId !== transaction.id
                              ? 'rotate(180deg)'
                              : '',
                          stroke: palette.grey.default,
                          fill: 'none',
                        },
                      }}
                    >
                      <Icon name="chevron" />
                    </div>
                  }
                />
              ))}
          </List>
          {selectedTxId && (
            <div
              css={{
                background: palette.white,
                padding: spacing.xxs,
                border: `1px solid ${palette.grey.x100}`,
                borderRadius: shape.borderRadius,
                display: 'flex',
                flexDirection: 'column',
                gap: spacing.xxs,
                position: 'absolute',
                width: '49%',
                right: 0,
                top: offsetTop ? offsetTop : 0,
              }}
            >
              {detailsLoading && <QBLoader />}
              {details && selectedTxId === details.id && (
                <>
                  {details.status === 'MANUAL_REVIEW' &&
                    details.type === TransactionType.SEND_EXTERNAL && (
                      <ManualReviewButtons
                        transactionId={details.id}
                        onApprove={() => {
                          void refreshBalances();
                          void refreshTransactions();
                          setSelectedTxId(undefined);
                        }}
                        onReject={() => {
                          void refreshBalances();
                          void refreshTransactions();
                          setSelectedTxId(undefined);
                        }}
                      />
                    )}
                  {Object.entries(details).map(([k, v]) => (
                    <TransactionItem
                      key={k}
                      name={k as keyof Transaction}
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                      value={v}
                    />
                  ))}
                  {details.type === TransactionType.RECEIVE_EXTERNAL && (
                    <Card>
                      <div
                        css={{
                          display: 'flex',
                          gap: spacing.xxs,
                          flexDirection: 'column',
                        }}
                      >
                        <Typography variant="bodyBold">Refund</Typography>
                        <Input
                          label="Destination address"
                          value={refundDestinationAddress}
                          onChange={(e) =>
                            setRefundDestinationAddress(e.target.value)
                          }
                          disabled={
                            !hasFCURefundPermission || refundCryptoLoading
                          }
                        />
                        {isPollingTravelRule && refundCryptoPreviewValue && (
                          <Markdown
                            markdown={`To comply with regulations, you need to provide information about the recipient. [Click here](${process.env.REACT_APP_QB_WEB_URL}/travel-rule-beneficiary-info/${refundCryptoPreviewValue.previewId}) to go to the form.`}
                            styles={{
                              color: palette.grey.default,
                              fontSize: '0.85rem',
                            }}
                          />
                        )}
                        {refundCryptoValue == null && (
                          <Button
                            text="Refund"
                            variant="primary"
                            loading={
                              refundCryptoPreviewLoading ||
                              refundCryptoLoading ||
                              isPollingTravelRule
                            }
                            disabled={
                              !hasFCURefundPermission ||
                              refundCryptoPreviewLoading ||
                              refundCryptoLoading ||
                              isPollingTravelRule
                            }
                            onClick={() =>
                              refundCryptoFn(
                                details.id,
                                refundDestinationAddress
                              )
                            }
                          />
                        )}
                        {refundCryptoValue != null && (
                          <span>Refund successful</span>
                        )}
                      </div>
                    </Card>
                  )}
                  <div css={{ display: 'flex', gap: spacing.xs }}>
                    <Button
                      text="Reupload to CM1"
                      variant="secondary"
                      loading={reuploadTXLoading}
                      disabled={reuploadTXLoading}
                      onClick={reuploadTxToCM1}
                    />
                    {details.type === TransactionType.BUY && (
                      <>
                        {refundValue == null && (
                          <Button
                            text="Refund"
                            variant="primary"
                            loading={refundLoading}
                            disabled={!hasCardRefundPermission || refundLoading}
                            onClick={() => refund(details.id)}
                          />
                        )}
                        {refundValue != null && (
                          <span>
                            Refund {refundValue ? 'successful' : 'failed'}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </>
      {refundError && !refundLoading && (
        <Snackbar
          variant="danger"
          message={formatErrorResponse(refundError)}
          open={showErrorAlert}
          onClose={() => setShowErrorAlert(false)}
        />
      )}
      {refundCryptoError && !refundCryptoLoading && (
        <Snackbar
          variant="danger"
          message={formatErrorResponse(refundCryptoError)}
          open={showErrorAlert}
          onClose={() => setShowErrorAlert(false)}
        />
      )}
      {refundCryptoPreviewError && !refundCryptoPreviewLoading && (
        <Snackbar
          variant="danger"
          message={formatErrorResponse(refundCryptoPreviewError)}
          open={showErrorAlert}
          onClose={() => setShowErrorAlert(false)}
        />
      )}
      {refundCryptoError && !refundCryptoLoading && (
        <Snackbar
          variant="danger"
          message={formatErrorResponse(refundCryptoError)}
          open={showErrorAlert}
          onClose={() => setShowErrorAlert(false)}
        />
      )}
      {travelRuleInfoError && !isPollingTravelRule && (
        <Snackbar
          variant="danger"
          message={formatErrorResponse(travelRuleInfoError)}
          open={showErrorAlert}
          onClose={() => setShowErrorAlert(false)}
        />
      )}
      {reuploadTxError && !reuploadTXLoading && (
        <Snackbar
          variant="danger"
          message={formatErrorResponse(reuploadTxError)}
          open={showErrorAlert}
          onClose={() => setShowErrorAlert(false)}
        />
      )}
    </>
  );
};
